import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';
import KnowledgeContent, {
  extractKnowledgeAttachmentIds,
  knowledgeAttachmentMarker,
  removeKnowledgeAttachmentMarker,
} from '../../components/knowledge/KnowledgeContent';
import KnowledgeAttachments from '../../components/knowledge/KnowledgeAttachments';
import KnowledgeMedia from '../../components/knowledge/KnowledgeMedia';
import { canUseCategoryAsParent, flattenCategoryTree } from '../../utils/knowledgeTree';

const KNOWLEDGE_FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.zip,.7z,.psd,.ai,.cdr,.mp4,.mov,.m4v,.webm,.avi,.mkv';

function byOrder(a, b) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name || a.title || '').localeCompare(String(b.name || b.title || ''), 'ru');
}

function normalizeRoles(value) {
  if (Array.isArray(value)) {
    return value.map(item => typeof item === 'string' ? { value: item, label: item } : item).filter(item => item?.value);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([role, label]) => ({ value: role, label }));
  }
  return [];
}

function normalizePlaces(value) {
  return (Array.isArray(value) ? value : []).map(item => (
    typeof item === 'string' ? { value: item, label: item } : item
  )).filter(item => item?.value);
}

function pointId(point) {
  return String(point?.id ?? point ?? '');
}

function flattenNetworkPoints(network) {
  const points = [];
  const seen = new Set();
  const append = (point, zone) => {
    const id = pointId(point);
    if (!id || seen.has(id)) return;
    seen.add(id);
    points.push({
      ...point,
      is_active: point?.is_active !== false && zone?.is_active !== false,
      zone_name: zone?.name || point.zone_name || '',
    });
  };

  (network?.zones || []).forEach(zone => (zone.points || []).forEach(point => append(point, zone)));
  (network?.points || []).forEach(point => append(point, null));
  return points.sort(byOrder);
}

function normalizeStringList(value) {
  return (Array.isArray(value) ? value : []).map(item => String(item?.value ?? item)).filter(Boolean);
}

function normalizePointIds(value) {
  return (Array.isArray(value) ? value : []).map(pointId).filter(Boolean);
}

function categoryArticles(category) {
  return [...(Array.isArray(category?.articles) ? category.articles : [])].sort(byOrder);
}

function articleAccess(article) {
  return {
    allowed_roles: normalizeStringList(article?.allowed_roles ?? article?.roles),
    allowed_networks: normalizeStringList(article?.allowed_networks ?? article?.networks),
    allowed_point_ids: normalizePointIds(article?.allowed_point_ids ?? article?.point_ids ?? article?.points),
    allowed_places: normalizeStringList(article?.allowed_places ?? article?.places),
  };
}

function hasAudience(access) {
  return ['allowed_roles', 'allowed_networks', 'allowed_point_ids', 'allowed_places']
    .some(field => Array.isArray(access?.[field]) && access[field].length > 0);
}

function audienceSummary(article, roles, networks, places) {
  const access = articleAccess(article);
  if (!hasAudience(access)) return 'Доступно всем';

  const roleMap = new Map(roles.map(role => [String(role.value), role.label]));
  const networkMap = new Map(networks.map(network => [String(network.slug ?? network.id), network.name]));
  const placeMap = new Map(places.map(place => [String(place.value), place.label]));
  const parts = [];

  if (access.allowed_roles.length) {
    parts.push(access.allowed_roles.map(value => roleMap.get(value) || value).join(', '));
  }
  if (access.allowed_networks.length) {
    parts.push(access.allowed_networks.map(value => networkMap.get(value) || value).join(', '));
  }
  if (access.allowed_point_ids.length) parts.push(`${access.allowed_point_ids.length} точ.`);
  if (access.allowed_places.length) {
    parts.push(access.allowed_places.map(value => placeMap.get(value) || value).join(', '));
  }
  return parts.join(' · ');
}

function errorText(error, fallback) {
  return error?.response?.data?.detail || fallback;
}

export default function KnowledgeBase() {
  const [catalog, setCatalog] = useState({ categories: [], roles: [], networks: [], places: [] });
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryDialog, setCategoryDialog] = useState(null);
  const [articleDialog, setArticleDialog] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (preferredCategoryId = null, options = {}) => {
    const silent = options.silent === true;
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/admin/knowledge');
      const categories = flattenCategoryTree(response.data.categories || []);
      const nextCatalog = {
        categories,
        roles: normalizeRoles(response.data.roles).filter(role => role.value !== 'admin'),
        networks: [...(response.data.networks || [])].sort(byOrder),
        places: normalizePlaces(response.data.places),
      };
      setCatalog(nextCatalog);
      setSelectedCategoryId(current => {
        const requested = preferredCategoryId ?? current;
        if (requested != null && categories.some(category => String(category.id) === String(requested))) return requested;
        return categories[0]?.id ?? null;
      });
    } catch (err) {
      setError(errorText(err, 'Не удалось загрузить базу знаний'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedCategory = catalog.categories.find(category => String(category.id) === String(selectedCategoryId)) || null;
  const articles = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    const source = selectedCategory ? categoryArticles(selectedCategory) : [];
    if (!needle) return source;
    return source.filter(article => `${article.title || ''} ${article.summary || ''}`.toLocaleLowerCase('ru').includes(needle));
  }, [query, selectedCategory]);

  const saveCategory = async (form, id) => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      parent_id: form.parent_id ? Number(form.parent_id) : null,
      sort_order: Number(form.sort_order) || 0,
      is_active: !!form.is_active,
    };
    const response = id
      ? await api.put(`/api/admin/knowledge/categories/${id}`, payload)
      : await api.post('/api/admin/knowledge/categories', payload);
    const savedId = response.data?.id ?? id;
    setCategoryDialog(null);
    await load(savedId);
  };

  const deleteCategory = async (category) => {
    const articlesCount = categoryArticles(category).length;
    const childrenCount = catalog.categories.filter(item => String(item.parent_id) === String(category.id)).length;
    if (articlesCount) {
      setError(`Сначала удалите или перенесите материалы из категории «${category.name}» (${articlesCount}).`);
      return;
    }
    if (childrenCount) {
      setError(`Сначала удалите или перенесите подкатегории из «${category.name}» (${childrenCount}).`);
      return;
    }
    if (!window.confirm(`Удалить пустую категорию «${category.name}»?`)) return;
    try {
      await api.delete(`/api/admin/knowledge/categories/${category.id}`);
      await load();
    } catch (err) {
      setError(errorText(err, 'Не удалось удалить категорию'));
    }
  };

  const saveArticle = async (payload, id) => {
    const response = id
      ? await api.put(`/api/admin/knowledge/articles/${id}`, payload)
      : await api.post('/api/admin/knowledge/articles', payload);
    await load(response.data?.category_id ?? payload.category_id);
    if (id) {
      setArticleDialog(null);
    } else {
      setArticleDialog(response.data);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Удалить статью «${article.title}»?`)) return;
    try {
      await api.delete(`/api/admin/knowledge/articles/${article.id}`);
      await load(selectedCategoryId);
    } catch (err) {
      setError(errorText(err, 'Не удалось удалить статью'));
    }
  };

  if (loading) return <div className="page-loader">Загрузка базы знаний...</div>;

  return (
    <div className="page knowledge-admin-page">
      <div className="page-header knowledge-page-header">
        <div>
          <h1>База знаний</h1>
          <p className="page-subtitle">Инструкции для сотрудников с доступом по ролям, сетям и точкам.</p>
        </div>
        <button className="btn-primary" onClick={() => setArticleDialog({ category_id: selectedCategoryId })} disabled={!selectedCategory}>
          + Новая статья
        </button>
      </div>

      {error && <div className="error-msg" role="alert">{error}</div>}

      <div className="knowledge-admin-layout">
        <aside className="knowledge-category-panel" aria-label="Категории базы знаний">
          <div className="knowledge-panel-heading">
            <div>
              <strong>Категории</strong>
              <span>{catalog.categories.length}</span>
            </div>
            <button type="button" className="knowledge-icon-button" onClick={() => setCategoryDialog({ parent_id: null })} aria-label="Добавить категорию" title="Добавить категорию">+</button>
          </div>

          {catalog.categories.length === 0 ? (
            <div className="knowledge-panel-empty">
              <p>Категорий пока нет.</p>
              <button className="btn-secondary small" onClick={() => setCategoryDialog({ parent_id: null })}>Создать первую</button>
            </div>
          ) : (
            <div className="knowledge-category-list">
              {catalog.categories.map(category => {
                const selected = String(category.id) === String(selectedCategoryId);
                const count = categoryArticles(category).length;
                const effectivelyInactive = category.is_effectively_active === false;
                return (
                  <div
                    key={category.id}
                    className={`knowledge-category-row ${selected ? 'selected' : ''} ${effectivelyInactive ? 'inactive' : ''}`}
                    style={{ '--knowledge-category-depth': category.tree_depth || 0 }}
                  >
                    <button type="button" className="knowledge-category-select" onClick={() => setSelectedCategoryId(category.id)} aria-pressed={selected}>
                      <span>{category.tree_depth ? '↳ ' : ''}{category.name}</span>
                      <small>{count}</small>
                    </button>
                    <div className="knowledge-row-actions">
                      {!category.parent_id && <button type="button" onClick={() => setCategoryDialog({ parent_id: category.id })} aria-label={`Добавить подкатегорию в ${category.name}`} title="Добавить подкатегорию">＋</button>}
                      <button type="button" onClick={() => setCategoryDialog(category)} aria-label={`Изменить категорию ${category.name}`} title="Изменить">✎</button>
                      <button type="button" onClick={() => deleteCategory(category)} aria-label={`Удалить категорию ${category.name}`} title="Удалить">×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        <section className="knowledge-articles-panel" aria-live="polite">
          {selectedCategory ? (
            <>
              <div className="knowledge-articles-heading">
                <div>
                  <div className="knowledge-title-line">
                    <h2>{selectedCategory.path_label || selectedCategory.name}</h2>
                    {selectedCategory.is_effectively_active === false && <span className="knowledge-status draft">Неактивна</span>}
                  </div>
                  {selectedCategory.description && <p>{selectedCategory.description}</p>}
                </div>
                <label className="knowledge-search-field">
                  <span className="sr-only">Поиск по статьям</span>
                  <span aria-hidden="true">⌕</span>
                  <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Найти статью" />
                </label>
              </div>

              {articles.length === 0 ? (
                <div className="knowledge-empty-state">
                  <div aria-hidden="true">📄</div>
                  <h3>{query ? 'Ничего не найдено' : 'В категории пока нет статей'}</h3>
                  <p>{query ? 'Попробуйте изменить поисковый запрос.' : 'Создайте первую инструкцию и настройте её аудиторию.'}</p>
                  {!query && <button className="btn-primary" onClick={() => setArticleDialog({ category_id: selectedCategory.id })}>+ Создать статью</button>}
                </div>
              ) : (
                <div className="knowledge-admin-article-list">
                  {articles.map(article => (
                    <article key={article.id} className={`knowledge-admin-article-card ${article.cover ? 'has-cover' : ''}`}>
                      {article.cover && <KnowledgeMedia attachment={article.cover} variant="cover" className="knowledge-admin-card-cover" />}
                      <button type="button" className="knowledge-article-main" onClick={() => setArticleDialog(article)}>
                        <span className={`knowledge-status ${article.is_published ? 'published' : 'draft'}`}>
                          {article.is_published ? 'Опубликована' : 'Черновик'}
                        </span>
                        <strong>{article.title}</strong>
                        <p>{article.summary || 'Краткое описание не заполнено'}</p>
                        <small>🔐 {audienceSummary(article, catalog.roles, catalog.networks, catalog.places)}</small>
                        {!!article.attachments?.length && <small>📎 Файлов: {article.attachments.length}</small>}
                      </button>
                      <div className="knowledge-article-meta">
                        <span>Порядок: {article.sort_order ?? 0}</span>
                        <div className="knowledge-row-actions">
                          <button type="button" onClick={() => setArticleDialog(article)} aria-label={`Изменить статью ${article.title}`} title="Изменить">✎</button>
                          <button type="button" onClick={() => deleteArticle(article)} aria-label={`Удалить статью ${article.title}`} title="Удалить">×</button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="knowledge-empty-state">
              <div aria-hidden="true">📚</div>
              <h3>Создайте категорию</h3>
              <p>Например, «Принтеры», «VPN» или «Рабочее место».</p>
            </div>
          )}
        </section>
      </div>

      {categoryDialog && (
        <CategoryDialog
          category={categoryDialog}
          categories={catalog.categories}
          onClose={() => setCategoryDialog(null)}
          onSave={saveCategory}
        />
      )}

      {articleDialog && (
        <ArticleDialog
          key={articleDialog.id || 'new'}
          article={articleDialog}
          categories={catalog.categories}
          roles={catalog.roles}
          networks={catalog.networks}
          places={catalog.places}
          onClose={() => setArticleDialog(null)}
          onSave={saveArticle}
          onRefresh={() => load(selectedCategoryId, { silent: true })}
        />
      )}
    </div>
  );
}

function CategoryDialog({ category, categories, onClose, onSave }) {
  const isEdit = !!category.id;
  const [form, setForm] = useState({
    name: category.name || '',
    description: category.description || '',
    parent_id: category.parent_id ?? '',
    sort_order: category.sort_order ?? 0,
    is_active: category.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const hasChildren = categories.some(item => String(item.parent_id) === String(category.id));
  const parentOptions = categories.filter(item => (
    item.parent_id == null && canUseCategoryAsParent(categories, item.id, category.id)
  ));

  useDialogLifecycle(onClose);

  const submit = async event => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Введите название категории');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form, category.id);
    } catch (err) {
      setError(errorText(err, 'Не удалось сохранить категорию'));
      setSaving(false);
    }
  };

  return (
    <div className="knowledge-dialog-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <form className="knowledge-dialog knowledge-category-dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="knowledge-category-title">
        <div className="knowledge-dialog-header">
          <div>
            <span className="knowledge-dialog-eyebrow">Категория</span>
            <h2 id="knowledge-category-title">{isEdit ? 'Изменить категорию' : 'Новая категория'}</h2>
          </div>
          <button type="button" className="knowledge-dialog-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>
        <div className="knowledge-dialog-body">
          {error && <div className="error-msg" role="alert">{error}</div>}
          <label className="form-group">
            <span>Название *</span>
            <input type="text" value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} autoFocus maxLength={150} />
          </label>
          <label className="form-group">
            <span>Описание</span>
            <textarea rows="3" value={form.description} onChange={event => setForm(current => ({ ...current, description: event.target.value }))} maxLength={500} />
          </label>
          <label className="form-group">
            <span>Родительская категория</span>
            <select
              value={form.parent_id}
              disabled={hasChildren}
              onChange={event => setForm(current => ({ ...current, parent_id: event.target.value }))}
            >
              <option value="">Нет — основная категория</option>
              {parentOptions.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <small className="knowledge-field-hint">{hasChildren ? 'У категории уже есть подкатегории, поэтому её нельзя вложить.' : 'Подкатегории отображаются внутри выбранной основной категории.'}</small>
          </label>
          <div className="knowledge-form-row">
            <label className="form-group">
              <span>Порядок</span>
              <input type="number" value={form.sort_order} onChange={event => setForm(current => ({ ...current, sort_order: event.target.value }))} />
            </label>
            <label className="knowledge-toggle-field">
              <input type="checkbox" checked={form.is_active} onChange={event => setForm(current => ({ ...current, is_active: event.target.checked }))} />
              <span>Категория активна</span>
            </label>
          </div>
        </div>
        <div className="knowledge-dialog-footer">
          <button type="button" className="btn-ghost" onClick={onClose}>Отмена</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
        </div>
      </form>
    </div>
  );
}

function articleForm(article) {
  const access = articleAccess(article);
  return {
    category_id: article.category_id ?? '',
    title: article.title || '',
    summary: article.summary || '',
    content: article.content || '',
    cover_attachment_id: article.cover_attachment_id || '',
    is_published: !!article.is_published,
    sort_order: article.sort_order ?? 0,
    ...access,
  };
}

function ArticleDialog({ article, categories, roles, networks, places, onClose, onSave, onRefresh }) {
  const isEdit = !!article.id;
  const [form, setForm] = useState(() => articleForm(article));
  const [visibility, setVisibility] = useState(() => hasAudience(articleAccess(article)) ? 'restricted' : 'all');
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState(() => [...(article.attachments || [])]);
  const [uploading, setUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [error, setError] = useState('');
  const textareaRef = useRef(null);
  const embeddedIds = useMemo(() => extractKnowledgeAttachmentIds(form.content), [form.content]);

  useDialogLifecycle(onClose);

  const toggleListValue = (field, value) => {
    const normalized = String(value);
    setForm(current => {
      const values = current[field] || [];
      return {
        ...current,
        [field]: values.includes(normalized) ? values.filter(item => item !== normalized) : [...values, normalized],
      };
    });
  };

  const toggleNetwork = network => {
    const value = String(network.slug ?? network.id);
    const networkPointIds = new Set(flattenNetworkPoints(network).map(pointId));
    setForm(current => {
      const selected = current.allowed_networks.includes(value);
      return {
        ...current,
        allowed_networks: selected
          ? current.allowed_networks.filter(item => item !== value)
          : [...current.allowed_networks, value],
        allowed_point_ids: selected
          ? current.allowed_point_ids
          : current.allowed_point_ids.filter(id => !networkPointIds.has(id)),
      };
    });
  };

  const setAudienceMode = mode => {
    setVisibility(mode);
    if (mode === 'all') {
      setForm(current => ({
        ...current,
        allowed_roles: [],
        allowed_networks: [],
        allowed_point_ids: [],
        allowed_places: [],
      }));
    }
  };

  const insertMarkdown = (before, after = '', fallback = 'текст') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.slice(start, end) || fallback;
    const next = `${form.content.slice(0, start)}${before}${selected}${after}${form.content.slice(end)}`;
    setForm(current => ({ ...current, content: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertAttachment = attachment => {
    const textarea = textareaRef.current;
    const marker = knowledgeAttachmentMarker(attachment.id);
    const start = textarea?.selectionStart ?? form.content.length;
    const prefix = form.content.slice(0, start);
    const suffix = form.content.slice(start);
    const before = prefix && !prefix.endsWith('\n\n') ? (prefix.endsWith('\n') ? '\n' : '\n\n') : '';
    const after = suffix && !suffix.startsWith('\n\n') ? (suffix.startsWith('\n') ? '\n' : '\n\n') : '';
    const insertion = `${before}${marker}${after}`;
    setForm(current => ({ ...current, content: `${prefix}${insertion}${suffix}` }));
    requestAnimationFrame(() => {
      const nextPosition = start + insertion.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const setCover = attachment => {
    setForm(current => ({ ...current, cover_attachment_id: attachment?.id || '' }));
  };

  const uploadAttachments = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !article.id) return;
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const body = new FormData();
        body.append('file', file);
        const response = await api.post(`/api/admin/knowledge/articles/${article.id}/attachments`, body);
        setAttachments(current => [...current, response.data]);
      }
      if (onRefresh) onRefresh().catch(() => {});
    } catch (err) {
      setError(errorText(err, 'Не удалось загрузить файл'));
      if (onRefresh) onRefresh().catch(() => {});
    } finally {
      setUploading(false);
    }
  };

  const deleteAttachment = async attachment => {
    if (!window.confirm(`Удалить файл «${attachment.name}»?`)) return;
    setDeletingAttachmentId(attachment.id);
    setError('');
    try {
      await api.delete(`/api/admin/knowledge/attachments/${attachment.id}`);
      setAttachments(current => current.filter(item => item.id !== attachment.id));
      setForm(current => ({
        ...current,
        cover_attachment_id: String(current.cover_attachment_id) === String(attachment.id) ? '' : current.cover_attachment_id,
        content: removeKnowledgeAttachmentMarker(current.content, attachment.id),
      }));
      if (onRefresh) onRefresh().catch(() => {});
    } catch (err) {
      setError(errorText(err, 'Не удалось удалить файл'));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const submit = async event => {
    event.preventDefault();
    if (!form.category_id) return setError('Выберите категорию');
    if (!form.title.trim()) return setError('Введите название статьи');
    if (!form.content.trim()) return setError('Добавьте содержимое статьи');
    if (visibility === 'restricted' && !hasAudience(form)) {
      return setError('Для ограниченного доступа выберите хотя бы одну роль, сеть, точку или место');
    }

    setSaving(true);
    setError('');
    const payload = {
      category_id: /^\d+$/.test(String(form.category_id)) ? Number(form.category_id) : form.category_id,
      title: form.title.trim(),
      summary: form.summary.trim(),
      content: form.content.trim(),
      cover_attachment_id: form.cover_attachment_id || null,
      allowed_roles: visibility === 'all' ? [] : form.allowed_roles,
      allowed_networks: visibility === 'all' ? [] : form.allowed_networks,
      allowed_point_ids: visibility === 'all' ? [] : form.allowed_point_ids.map(id => /^\d+$/.test(id) ? Number(id) : id),
      allowed_places: visibility === 'all' ? [] : form.allowed_places,
      is_published: !!form.is_published,
      sort_order: Number(form.sort_order) || 0,
    };

    try {
      await onSave(payload, article.id);
    } catch (err) {
      setError(errorText(err, 'Не удалось сохранить статью'));
      setSaving(false);
    }
  };

  return (
    <div className="knowledge-dialog-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <form className="knowledge-dialog knowledge-article-dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="knowledge-article-title">
        <div className="knowledge-dialog-header">
          <div>
            <span className="knowledge-dialog-eyebrow">Статья</span>
            <h2 id="knowledge-article-title">{isEdit ? 'Редактирование' : 'Новая инструкция'}</h2>
          </div>
          <button type="button" className="knowledge-dialog-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="knowledge-dialog-body knowledge-article-dialog-body">
          {error && <div className="error-msg" role="alert">{error}</div>}

          <div className="knowledge-editor-fields">
            <div className="knowledge-form-row knowledge-form-row-main">
              <label className="form-group">
                <span>Категория *</span>
                <select value={form.category_id} onChange={event => setForm(current => ({ ...current, category_id: event.target.value }))}>
                  <option value="">Выберите категорию</option>
                  {categories.map(category => <option key={category.id} value={category.id}>{category.tree_depth ? '↳ ' : ''}{category.path_label || category.name}{category.is_effectively_active === false ? ' (неактивна)' : ''}</option>)}
                </select>
              </label>
              <label className="form-group knowledge-order-field">
                <span>Порядок</span>
                <input type="number" value={form.sort_order} onChange={event => setForm(current => ({ ...current, sort_order: event.target.value }))} />
              </label>
            </div>

            <label className="form-group">
              <span>Название *</span>
              <input type="text" value={form.title} onChange={event => setForm(current => ({ ...current, title: event.target.value }))} autoFocus maxLength={220} placeholder="Например: Как сбросить счётчик принтера" />
            </label>

            <label className="form-group">
              <span>Краткое описание</span>
              <textarea rows="2" value={form.summary} onChange={event => setForm(current => ({ ...current, summary: event.target.value }))} maxLength={500} placeholder="Показывается в списке статей" />
              <small className="knowledge-field-hint">{form.summary.length}/500</small>
            </label>

            <div className="form-group">
              <span className="knowledge-field-label">Текст статьи *</span>
              <div className="knowledge-editor-toolbar" aria-label="Форматирование Markdown">
                <button type="button" onClick={() => insertMarkdown('## ', '', 'Заголовок')} title="Заголовок">H2</button>
                <button type="button" onClick={() => insertMarkdown('**', '**')} title="Полужирный"><strong>B</strong></button>
                <button type="button" onClick={() => insertMarkdown('*', '*')} title="Курсив"><em>I</em></button>
                <button type="button" onClick={() => insertMarkdown('`', '`', 'команда')} title="Код">{'</>'}</button>
                <button type="button" onClick={() => insertMarkdown('- ', '', 'Шаг инструкции')} title="Список">• список</button>
                <button type="button" onClick={() => insertMarkdown('[', '](https://)', 'ссылка')} title="Ссылка">🔗</button>
                <button type="button" onClick={() => insertMarkdown('![', '](https://)', 'описание изображения')} title="Изображение">🖼</button>
              </div>
              <div className="knowledge-editor-grid">
                <label className="knowledge-editor-pane">
                  <span>Markdown</span>
                  <textarea ref={textareaRef} rows="18" value={form.content} onChange={event => setForm(current => ({ ...current, content: event.target.value }))} maxLength={100000} placeholder={'## Шаг 1\n\nОпишите действие...\n\n```\nipconfig /flushdns\n```'} spellCheck="true" />
                </label>
                <div className="knowledge-preview-pane">
                  <span>Предпросмотр</span>
                  <div className="knowledge-preview-scroll">
                    <KnowledgeContent content={form.content} attachments={attachments} emptyText="Начните писать — здесь появится предпросмотр." />
                  </div>
                </div>
              </div>
              <small className="knowledge-field-hint">Поддерживаются заголовки, списки, ссылки, изображения, таблицы, цитаты и блоки кода.</small>
            </div>

            <section className="knowledge-file-editor" aria-labelledby="knowledge-files-title">
              <div className="knowledge-file-editor-heading">
                <div>
                  <h3 id="knowledge-files-title">Файлы статьи</h3>
                  <p>Загрузите файл, затем вставьте его в нужное место текста. Изображение можно выбрать обложкой карточки.</p>
                </div>
                {isEdit && (
                  <label className={`btn-secondary knowledge-file-upload ${uploading ? 'disabled' : ''}`}>
                    {uploading ? 'Загрузка…' : '+ Добавить файлы'}
                    <input type="file" multiple accept={KNOWLEDGE_FILE_ACCEPT} onChange={uploadAttachments} disabled={uploading} />
                  </label>
                )}
              </div>
              {!isEdit ? (
                <div className="knowledge-file-empty">Сначала создайте статью — после сохранения здесь сразу откроется загрузка файлов.</div>
              ) : attachments.length ? (
                <KnowledgeAttachments
                  attachments={attachments}
                  onInsert={insertAttachment}
                  embeddedIds={embeddedIds}
                  onSetCover={setCover}
                  coverAttachmentId={form.cover_attachment_id}
                  onDelete={deleteAttachment}
                  deletingId={deletingAttachmentId}
                  title="Прикреплённые файлы"
                />
              ) : (
                <div className="knowledge-file-empty">Файлов пока нет. Можно загрузить документы, таблицы, PDF, изображения, архивы или видео до 500 МБ.</div>
              )}
            </section>
          </div>

          <AudienceEditor
            form={form}
            visibility={visibility}
            setVisibility={setAudienceMode}
            roles={roles}
            networks={networks}
            places={places}
            toggleListValue={toggleListValue}
            toggleNetwork={toggleNetwork}
          />
        </div>

        <div className="knowledge-dialog-footer knowledge-article-footer">
          <label className="knowledge-publish-toggle">
            <input type="checkbox" checked={form.is_published} onChange={event => setForm(current => ({ ...current, is_published: event.target.checked }))} />
            <span>{form.is_published ? 'Опубликовать для выбранной аудитории' : 'Сохранить как черновик'}</span>
          </label>
          <div>
            <button type="button" className="btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : isEdit ? 'Сохранить статью' : 'Создать и добавить файлы'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function AudienceEditor({ form, visibility, setVisibility, roles, networks, places, toggleListValue, toggleNetwork }) {
  return (
    <section className="knowledge-audience-editor" aria-labelledby="knowledge-audience-title">
      <div className="knowledge-audience-heading">
        <div>
          <h3 id="knowledge-audience-title">Доступ к статье</h3>
          <p>Значения внутри группы работают как ИЛИ. Если выбраны роли и локации, пользователь должен совпасть с ролью И хотя бы с одной локацией. Администратор видит всё.</p>
        </div>
      </div>

      <div className="knowledge-visibility-options">
        <label className={visibility === 'all' ? 'selected' : ''}>
          <input type="radio" name="knowledge-visibility" checked={visibility === 'all'} onChange={() => setVisibility('all')} />
          <span><strong>Для всех</strong><small>Все одобренные пользователи портала</small></span>
        </label>
        <label className={visibility === 'restricted' ? 'selected' : ''}>
          <input type="radio" name="knowledge-visibility" checked={visibility === 'restricted'} onChange={() => setVisibility('restricted')} />
          <span><strong>Ограничить</strong><small>Выбрать роли, сети, точки или места</small></span>
        </label>
      </div>

      {visibility === 'restricted' && (
        <div className="knowledge-audience-groups">
          <AudienceGroup title="Роли" hint="Выберите одну или несколько ролей">
            <div className="knowledge-check-grid">
              {roles.map(role => (
                <label key={role.value} className={form.allowed_roles.includes(String(role.value)) ? 'checked' : ''}>
                  <input type="checkbox" checked={form.allowed_roles.includes(String(role.value))} onChange={() => toggleListValue('allowed_roles', role.value)} />
                  <span>{role.label}</span>
                </label>
              ))}
            </div>
          </AudienceGroup>

          <AudienceGroup title="Сети и точки" hint="Сеть открывает все её активные точки; либо отметьте отдельные точки">
            <div className="knowledge-network-access-list">
              {networks.map(network => {
                const networkValue = String(network.slug ?? network.id);
                const points = flattenNetworkPoints(network);
                const wholeNetwork = form.allowed_networks.includes(networkValue);
                const networkInactive = network.is_active === false;
                const existingNetworkSelection = wholeNetwork;
                return (
                  <div key={networkValue} className={`knowledge-network-access ${networkInactive ? 'inactive' : ''}`}>
                    <label className="knowledge-network-checkbox">
                      <input
                        type="checkbox"
                        checked={wholeNetwork}
                        disabled={networkInactive && !existingNetworkSelection}
                        onChange={() => toggleNetwork(network)}
                      />
                      <span><strong>{network.name}</strong>{networkInactive && <small>Неактивна</small>}</span>
                    </label>
                    {points.length > 0 && (
                      <div className="knowledge-point-checks">
                        {points.map(point => {
                          const id = pointId(point);
                          const checked = form.allowed_point_ids.includes(id);
                          const inactive = networkInactive || point.is_active === false;
                          return (
                            <label key={id} className={`${checked ? 'checked' : ''} ${inactive ? 'inactive' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={wholeNetwork || (inactive && !checked)}
                                onChange={() => toggleListValue('allowed_point_ids', id)}
                              />
                              <span>{point.name}{point.zone_name ? <small>{point.zone_name}</small> : null}{inactive ? <small>Неактивна</small> : null}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </AudienceGroup>

          {places.length > 0 && (
            <AudienceGroup title="Отдельные места" hint="Для сотрудников офиса и склада">
              <div className="knowledge-check-grid compact">
                {places.map(place => (
                  <label key={place.value} className={form.allowed_places.includes(String(place.value)) ? 'checked' : ''}>
                    <input type="checkbox" checked={form.allowed_places.includes(String(place.value))} onChange={() => toggleListValue('allowed_places', place.value)} />
                    <span>{place.label}</span>
                  </label>
                ))}
              </div>
            </AudienceGroup>
          )}
        </div>
      )}
    </section>
  );
}

function AudienceGroup({ title, hint, children }) {
  return (
    <fieldset className="knowledge-audience-group">
      <legend>{title}</legend>
      <p>{hint}</p>
      {children}
    </fieldset>
  );
}

function useDialogLifecycle(onClose) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);
}
